const normalizeTeamName = (value) => String(value || '').trim();

/**
 * Bepaalt welke wedstrijd het overzicht als eerste toont.
 *
 * Op een thuiswedstrijddag krijgt die wedstrijd voorrang, ook wanneer de
 * aftrap later op de dag is. Op andere dagen tonen we de meest recente
 * gespeelde wedstrijd. Als er nog geen wedstrijd is gespeeld, valt de keuze
 * terug op de eerstvolgende wedstrijd.
 */
export const getDefaultOverviewMatchId = (matches, now = new Date()) => {
  if (!Array.isArray(matches) || matches.length === 0) return null;

  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const startOfTomorrow = new Date(startOfToday);
  startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);

  const datedMatches = matches
    .map((match) => ({ match, date: new Date(match.date) }))
    .filter(({ date }) => !Number.isNaN(date.getTime()))
    .sort((a, b) => a.date - b.date);

  const todaysHomeMatch = datedMatches.find(
    ({ match, date }) =>
      match.location !== 'away'
      && date >= startOfToday
      && date < startOfTomorrow,
  );
  if (todaysHomeMatch) return todaysHomeMatch.match.id;

  const previousMatches = datedMatches.filter(({ date }) => date < startOfToday);
  if (previousMatches.length) {
    return previousMatches[previousMatches.length - 1].match.id;
  }

  return datedMatches[0]?.match.id || null;
};

// Alleen de huidige bijeenkomst en de twee bijeenkomsten direct daarvoor.
export const getRecentSelectableMatches = (matches, now = new Date()) => {
  const currentId = getDefaultOverviewMatchId(matches, now);
  const current = (matches || []).find((match) => match.id === currentId);
  if (!current) return [];

  const previous = matches
    .filter((match) => match.id !== currentId
      && new Date(match.date) < new Date(current.date)
      && hasMatchStartedOrIsToday(match, now))
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 2);

  return [current, ...previous];
};

/**
 * Bestellingen mogen op de dag van een bijeenkomst al vóór de aanvang worden
 * ingevoerd. Vergelijk daarom kalenderdagen in plaats van het exacte tijdstip.
 */
export const hasMatchStartedOrIsToday = (match, now = new Date()) => {
  const matchDate = new Date(match?.date);
  if (Number.isNaN(matchDate.getTime())) return false;

  const startOfTomorrow = new Date(now);
  startOfTomorrow.setHours(0, 0, 0, 0);
  startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);

  return matchDate < startOfTomorrow;
};

export const getMatchClubName = (match) => {
  if (!match) return '';

  const explicitClubName = normalizeTeamName(
    match.club_name
      || match.clubName
      || match.team_name
      || match.teamName
      || (match.location === 'away' ? match.away_team : match.home_team),
  );
  if (explicitClubName) return explicitClubName;

  const opponent = normalizeTeamName(match.opponent).toLocaleLowerCase('nl-NL');
  const labelParts = String(match.label || '')
    .split('·')
    .map(normalizeTeamName)
    .filter(Boolean);

  if (
    labelParts.length >= 2
    && labelParts[labelParts.length - 1].toLocaleLowerCase('nl-NL') === opponent
  ) {
    return labelParts[0];
  }

  return '';
};

export const formatMatchTeams = (match) => {
  if (!match) return '';

  const explicitHomeTeam = normalizeTeamName(match.home_team);
  const explicitAwayTeam = normalizeTeamName(match.away_team);
  if (explicitHomeTeam && explicitAwayTeam) {
    return `${explicitHomeTeam} – ${explicitAwayTeam}`;
  }

  const opponent = match.opponent || 'Onbekende tegenstander';
  const clubName = getMatchClubName(match);
  if (!clubName) return opponent;

  return match.location === 'away'
    ? `${opponent} – ${clubName}`
    : `${clubName} – ${opponent}`;
};

export const formatMatchLabel = (match, dateOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
}) => {
  if (!match) return '';

  const date = new Date(match.date);
  const dateLabel = Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString('nl-NL', dateOptions);

  return dateLabel
    ? `${formatMatchTeams(match)} (${dateLabel})`
    : formatMatchTeams(match);
};
